import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { startGoogleLogin } from '../utils/auth';

export default function LoginPage() {
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(location.state?.error || null);

  useEffect(() => {
    if (location.state?.error) setError(location.state.error);
  }, [location.state]);

  const handleGoogle = async () => {
    setError(null);
    setIsRedirecting(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      await startGoogleLogin();
    } catch (err) {
      console.error('Login failed:', err);
      setError('Could not start sign-in. Please try again.');
      setIsRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 shadow-elegant" data-testid="login-card">
        <div className="flex flex-col items-center text-center space-y-6">
          {/* Logo */}
          <img
            src="/logo.png"
            alt="Foresight Logo"
            className="w-20 h-20 rounded-full object-cover"
          />

          {/* Title */}
          <div className="space-y-1">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
              <span className="text-primary">Foresight</span>
            </h1>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
              Meet Schedule Predictor
            </h2>
            <p className="text-muted-foreground text-sm pt-2">
              Know your meet schedule!
            </p>
          </div>

          {error && (
            <p
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 w-full"
              data-testid="login-error"
            >
              {error}
            </p>
          )}

          {/* Sign-in buttons */}
          <div className="w-full space-y-3 pt-2">
            <Button
              onClick={handleGoogle}
              variant="outline"
              disabled={isRedirecting}
              className="w-full h-12 text-base font-medium transition-smooth hover:bg-secondary"
              data-testid="login-google-button"
            >
              {isRedirecting ? (
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
              ) : (
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              {isRedirecting ? 'Redirecting to Google…' : 'Continue with Google'}
            </Button>

            {/* Apple Sign In — placeholder, enabled once iOS build is set up.
                Hidden on Android/web for now since it's not yet functional. */}
            <Button
              variant="outline"
              disabled
              className="w-full h-12 text-base font-medium opacity-60 cursor-not-allowed hidden"
              data-testid="login-apple-button"
            >
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple (coming soon)
            </Button>
          </div>

          {/* Footer text */}
          <p className="text-xs text-muted-foreground pt-4">
            By continuing, you agree to our{' '}
            <a
              href="/terms"
              className="underline hover:text-foreground"
              data-testid="login-terms-link"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="/privacy"
              className="underline hover:text-foreground"
              data-testid="login-privacy-link"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
}
